import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { scoutEndpoints } from '@/modules/scout-sourcing/constants';
import type {
  PersonaVoteRecord,
  ScoutConfirmResponse,
  ScoutMarketSizeResponse,
  ScoutNextRoundResponse,
  ScoutPreviewCandidatesResponse,
  ScoutPreviewChampionQueryResponse,
  ScoutSearchCriteria,
  ScoutStartFromPromptResponse,
  ScoutStartResponse,
  ScoutTargetingSessionResponse,
} from '@/modules/scout-sourcing/types';

type ScoutRequestInit = {
  params?: { path?: Record<string, string> };
  body?: unknown;
};

type ScoutFetchResponse = {
  data?: unknown;
  error?: unknown;
  response: Response;
};

const scoutGet = fetchClient.GET as unknown as (
  path: string,
  init?: ScoutRequestInit,
) => Promise<ScoutFetchResponse>;

const scoutPost = fetchClient.POST as unknown as (
  path: string,
  init?: ScoutRequestInit,
) => Promise<ScoutFetchResponse>;

export class ScoutApiError extends Error {
  readonly status: number;
  readonly responseText: string;

  constructor(message: string, status: number, responseText: string) {
    super(message);
    this.name = 'ScoutApiError';
    this.status = status;
    this.responseText = responseText;
  }
}

function getErrorStatus(error: unknown) {
  if (error instanceof ScoutApiError) {
    return error.status;
  }
  if (!(error instanceof Error)) {
    return null;
  }

  const match = error.message.match(/^HTTP (\d+):/);
  return match?.[1] ? Number(match[1]) : null;
}

function getResponseText(error: unknown) {
  if (error instanceof ScoutApiError) {
    return error.responseText;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function throwScoutApiError(path: string, error: unknown): never {
  if (error instanceof ScoutApiError) {
    throw error;
  }

  const status = getErrorStatus(error);
  if (!status) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error));
  }

  const responseText = getResponseText(error);
  throw new ScoutApiError(
    `Scout API ${path} failed (${status}): ${responseText}`,
    status,
    responseText,
  );
}

function pathParams(id: string): ScoutRequestInit['params'] {
  return { path: { id } };
}

async function postJson<TResponse>(
  path: string,
  body: unknown,
  params?: ScoutRequestInit['params'],
): Promise<TResponse> {
  try {
    const { data, error, response } = await scoutPost(path, { body, params });
    if (!response.ok) {
      const responseText = getResponseText(error);
      throw new ScoutApiError(
        `Scout API ${path} failed (${response.status}): ${responseText}`,
        response.status,
        responseText,
      );
    }
    return data as TResponse;
  } catch (error) {
    throwScoutApiError(path, error);
  }
}

async function getJson<TResponse>(
  path: string,
  params?: ScoutRequestInit['params'],
): Promise<TResponse | null> {
  try {
    const { data, error, response } = await scoutGet(path, { params });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const responseText = getResponseText(error);
      throw new ScoutApiError(
        `Scout API ${path} failed (${response.status}): ${responseText}`,
        response.status,
        responseText,
      );
    }
    return data as TResponse;
  } catch (error) {
    if (getErrorStatus(error) === 404) {
      return null;
    }
    throwScoutApiError(path, error);
  }
}

export const scoutClient = {
  getSession: (jobId: string) =>
    getJson<ScoutTargetingSessionResponse>(
      scoutEndpoints.session,
      pathParams(jobId),
    ),

  startFromPrompt: (prompt: string, companyId?: string) =>
    postJson<ScoutStartFromPromptResponse>(scoutEndpoints.startFromPrompt, {
      prompt,
      companyId: companyId ?? null,
    }),

  start: (jobId: string, prompt: string) =>
    postJson<ScoutStartResponse>(
      scoutEndpoints.start,
      {
        prompt,
      },
      pathParams(jobId),
    ),

  nextRound: (
    jobId: string,
    jobSourcingId: string,
    votesForPreviousRound: PersonaVoteRecord[] | null,
  ) =>
    postJson<ScoutNextRoundResponse>(
      scoutEndpoints.nextRound,
      {
        jobSourcingId,
        votesForPreviousRound,
      },
      pathParams(jobId),
    ),

  // Loads the hash-verified canonical snapshot saved by previewMarketSize.
  // The browser intentionally sends only jobSourcingId, never query bodies.
  confirm: (
    jobId: string,
    jobSourcingId: string,
    maxCandidates?: number,
    acceptOutOfRangeMarket?: boolean,
  ) =>
    postJson<ScoutConfirmResponse>(
      scoutEndpoints.confirm,
      {
        jobSourcingId,
        maxCandidates: maxCandidates ?? null,
        acceptOutOfRangeMarket: acceptOutOfRangeMarket ?? false,
      },
      pathParams(jobId),
    ),

  previewQuery: (jobId: string, jobSourcingId: string) =>
    postJson<ScoutPreviewChampionQueryResponse>(
      scoutEndpoints.previewQuery,
      {
        jobSourcingId,
      },
      pathParams(jobId),
    ),

  // Compiles structured criteria server-side and persists the canonical query
  // bodies and hash under jobSourcingId for later preview and confirmation.
  previewMarketSize: (
    jobId: string,
    jobSourcingId: string,
    searchCriteria: ScoutSearchCriteria,
  ) =>
    postJson<ScoutMarketSizeResponse>(
      scoutEndpoints.previewMarketSize,
      {
        jobSourcingId,
        searchCriteria,
      },
      pathParams(jobId),
    ),

  // Loads the hash-verified canonical snapshot saved by previewMarketSize.
  // The browser intentionally sends only jobSourcingId, never query bodies.
  previewCandidates: (
    jobId: string,
    jobSourcingId: string,
    maxCandidates?: number,
  ) =>
    postJson<ScoutPreviewCandidatesResponse>(
      scoutEndpoints.previewCandidates,
      {
        jobSourcingId,
        maxCandidates: maxCandidates ?? null,
      },
      pathParams(jobId),
    ),

  abandon: (jobId: string, jobSourcingId: string) =>
    postJson<void>(
      scoutEndpoints.abandon,
      {
        jobSourcingId,
      },
      pathParams(jobId),
    ),
};
